import moment from "moment";
import * as range from "moment-range";

export const momentRange = range.extendMoment(moment as any);

export type DateUnionType = (Date | string);
type DateMomentUnionType = (Date | string | moment.Moment);

type NullableDateType = string | Date | null | undefined;

export class DateUtils {
// eslint-disable-next-line sonarjs/public-static-readonly
    static dateMonthformat = "YYYY-MM";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static dateformat = "YYYY-MM-DD";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static datetimeformat = "YYYY-MM-DDTHH:mm:ss.SSS";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static datetimeformatWithoutMillis = "YYYY-MM-DD HH:mm:ss";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static datetimeformatWithoutSeconds = "YYYY-MM-DD HH:mm";

    // eslint-disable-next-line sonarjs/public-static-readonly
    static endOfDay = (datum: DateUnionType): string => moment(datum).endOf("day").format();

    // eslint-disable-next-line sonarjs/public-static-readonly
    static startOfDayDateTime = (datum: DateUnionType): string => moment(datum)
        .set({
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0
        })
        .format(DateUtils.datetimeformat);

    // eslint-disable-next-line sonarjs/public-static-readonly
    static endOfDayDateTime = (datum: DateUnionType): string => moment(datum)
        .set({
            hours: 23,
            minutes: 59,
            seconds: 59,
            milliseconds: 999
        })
        .format(DateUtils.datetimeformat);

    // eslint-disable-next-line sonarjs/public-static-readonly
    static shortDateString = (datum: DateMomentUnionType): string => datum ? moment(datum).format(DateUtils.dateformat) : null;

    // eslint-disable-next-line sonarjs/public-static-readonly
    static formatDateString = (datum: DateUnionType, formatString: string): string => datum ? moment(datum).format(formatString) : null;

    // eslint-disable-next-line sonarjs/public-static-readonly
    static formatUtcAsLocal =(datum: DateUnionType, formatString: string): string => datum ? moment.utc(datum).local().format(formatString) : null;

    static getStartOfMonth(): string {
        return moment().startOf("month").toISOString();
    }

    static getStartOfNextMonth(): string {
        return moment().add(1, "month").startOf("month").toISOString();
    }

    static endOfMonth(datum: DateMomentUnionType) {
        return moment(datum).endOf("month").format();
    }

    static daysBetween(start: DateMomentUnionType, end: DateMomentUnionType) {
        // Avrundas så det blir 1.95 (47 timmar) när man går från vintertid till sommartid
        // och motsvarande för sommartid till vintertid. 1.95 dag räknas annar som 1 dag i moment
        // om man inte har "precise"-flaggan
        return Math.round(moment(start).diff(moment(end), "d", true));
    }

    static isBeforeByDay(left: DateMomentUnionType, right: DateMomentUnionType): boolean {
        return moment(left).isBefore(moment(right), "day");
    }

    static isAfterByDay(left: DateMomentUnionType, right: DateMomentUnionType): boolean {
        return moment(left).isAfter(moment(right), "day");
    }

    static minutesBetween(start: DateMomentUnionType, end: DateMomentUnionType) {
        return moment(end).diff(moment(start), "minutes");
    }

    static now(): Date {
        return moment().toDate();
    }

    static nowAddMonths(months: number): Date {
        return moment().add(months, "month").toDate();
    }

    static nowAddDays(days: number): Date {
        return moment().add(days, "day").toDate();
    }

    static AddDays(date: Date, days: number): Date {
        return moment(date).add(days, "day").toDate();
    }

    static countDaysOfType(start: moment.Moment, end: moment.Moment, shouldIncrement: (date: string | moment.Moment) => boolean, excludeEnd = true) {
        const dateRange = momentRange.range(start, end);
        const days = Array.from(dateRange.by("d", { excludeEnd: excludeEnd }));
        return days.reduce((total, curr) => total + (shouldIncrement(curr) ? 1 : 0), 0);
    }

    static getMaxDateString(): string {
        const maxDate = new Date(9998, 12, 31, 23, 59, 59, 999);
        return DateUtils.formatDateString(maxDate, DateUtils.datetimeformat); // --> 9999-12-31 ...
    }

    static getMonthName(date: Date) {
        const d = new Date(date);

        const monthNames = [
            "Januari",
            "Februari",
            "Mars",
            "April",
            "Maj",
            "Juni",
            "Juli",
            "Augusti",
            "September",
            "Oktober",
            "November",
            "December"
        ];

        return monthNames[d.getMonth()];
    }

    // Handles string, Date, null and undefined — syftar till att vara kompatibel med net10-api
    static nullableDate(value: NullableDateType): string | null {
        if (typeof value === "string") {
            return value.trim() === "" ? null : value;
        }

        if (value instanceof Date) {
            return value.toISOString();
        }

        return null;
    }
}
